import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { TextField, Button, IconButton, Box, Typography } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { doc, updateDoc, addDoc, collection } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firestore as db } from "../components/firebase.config"


const ChecklistForm = ({ email, onClose, selectedChecklist }) => {
  const [checklistName, setChecklistName] = useState("");
  const [description, setDescription] = useState("");
  const [remarks, setRemarks] = useState("");
  const [imageUrl, setImageUrl] = useState(""); // Store the image URL here
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);

  const storage = getStorage();

  useEffect(() => {
    if (selectedChecklist) {
      setChecklistName(selectedChecklist.name || "");
      setDescription(selectedChecklist.description || "");
      setRemarks(selectedChecklist.remarks || "");
      setImageUrl(selectedChecklist.imageUrl || "");
      setColumns(selectedChecklist.columns || []);
      setRows(selectedChecklist.rows || []);
    } else {
      setColumns([
        { id: uuidv4(), name: "Item ID" },
        { id: uuidv4(), name: "Product Description" },
        { id: uuidv4(), name: "Quantity" },
        { id: uuidv4(), name: "Checkbox" },
      ]);
      setRows([{ id: uuidv4(), data: { Checkbox: false } }]);
    }
  }, [selectedChecklist]);

  const addColumn = () => {
    setColumns([...columns, { id: uuidv4(), name: "New Column" }]);
  };

  const removeColumn = (columnId) => {
    setColumns(columns.filter((col) => col.id !== columnId));
    setRows(
      rows.map((row) => {
        const updatedData = { ...row.data };
        delete updatedData[columnId];
        return { ...row, data: updatedData };
      })
    );
  };

  const addRow = () => {
    const newRow = { id: uuidv4(), data: { Checkbox: false } };
    setRows([...rows, newRow]);
  };

  const removeRow = (rowId) => {
    setRows(rows.filter((row) => row.id !== rowId));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const uniqueFileName = `${Date.now()}-${file.name}`;
        const imageRef = ref(storage, `checklist_images/${uniqueFileName}`);
        await uploadBytes(imageRef, file);
        const url = await getDownloadURL(imageRef);
        setImageUrl(url); // Update the image URL after upload
        console.log("Image uploaded successfully:", url);
      } catch (error) {
        console.error("Image upload failed:", error);
      }
    }
  };

  const removeImage = () => {
    setImageUrl("");
  };

  const handleSave = async () => {
    try {
      if (!imageUrl && selectedChecklist?.imageUrl) {
        // If the user removes the image, update Firebase to clear the imageUrl
        setImageUrl("");
      }

      if (selectedChecklist) {
        await updateDoc(doc(db, "checklists", selectedChecklist.id), {
          name: checklistName,
          description,
          remarks,
          imageUrl,
          columns,
          rows,
        });
      } else {
        await addDoc(collection(db, "checklists"), {
          name: checklistName,
          description,
          remarks,
          imageUrl,
          email,
          columns,
          rows,
        });
      }
      onClose();
    } catch (error) {
      console.error("Error saving checklist:", error);
    }
  };

  return (
    <Box
      sx={{
        padding: "2rem",
        background: "white",
        borderRadius: "8px",
        width: "90%",
        maxWidth: "1000px",
        margin: "0 auto",
        overflowY: "auto",
      }}
    >
      <Typography variant="h5" gutterBottom>
        {selectedChecklist ? "Edit Checklist" : "Create Checklist"}
      </Typography>
      <TextField
        label="Checklist Name"
        variant="outlined"
        value={checklistName}
        onChange={(e) => setChecklistName(e.target.value)}
        fullWidth
        sx={{ marginBottom: "1rem" }}
      />
      <TextField
        label="Description"
        variant="outlined"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        rows={3}
        sx={{ marginBottom: "1rem" }}
      />
      <Box sx={{ marginBottom: "1rem", overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.id} style={{ border: "1px solid #ddd", padding: "8px" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      value={col.name}
                      onChange={(e) =>
                        setColumns(
                          columns.map((c) =>
                            c.id === col.id ? { ...c, name: e.target.value } : c
                          )
                        )
                      }
                      fullWidth
                      size="small"
                    />
                    {columns.length > 4 && (
                      <IconButton onClick={() => removeColumn(col.id)}>
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                </th>
              ))}
              <th>
                <Button variant="outlined" startIcon={<Add />} onClick={addColumn}>
                  Add Column
                </Button>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                {columns.map((col) => (
                  <td key={col.id} style={{ border: "1px solid #ddd", padding: "8px" }}>
                    {col.name === "Checkbox" ? (
                      <input
                        type="checkbox"
                        checked={row.data[col.id] || false}
                        onChange={(e) =>
                          setRows(
                            rows.map((r) =>
                              r.id === row.id
                                ? { ...r, data: { ...r.data, [col.id]: e.target.checked } }
                                : r
                            )
                          )
                        }
                      />
                    ) : (
                      <TextField
                        value={row.data[col.id] || ""}
                        onChange={(e) =>
                          setRows(
                            rows.map((r) =>
                              r.id === row.id
                                ? { ...r, data: { ...r.data, [col.id]: e.target.value } }
                                : r
                            )
                          )
                        }
                        fullWidth
                        size="small"
                      />
                    )}
                  </td>
                ))}
                <td>
                  <IconButton onClick={() => removeRow(row.id)}>
                    <Delete />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Button variant="outlined" startIcon={<Add />} onClick={addRow}>
        Add Row
      </Button>
      <Box sx={{ marginTop: "1rem" }}>
        <input type="file" onChange={handleImageUpload} />
        {imageUrl && (
          <Box sx={{ marginTop: "1rem" }}>
            <img
              src={imageUrl}
              alt="Uploaded"
              style={{ maxWidth: "150px", marginBottom: "1rem" }}
            />
            <Button variant="outlined" color="secondary" onClick={removeImage}>
              Remove Image
            </Button>
          </Box>
        )}
      </Box>
      <TextField
        label="Remarks"
        variant="outlined"
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        fullWidth
        multiline
        rows={3}
        sx={{ marginTop: "1rem" }}
      />
      <Box sx={{ marginTop: "1rem" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          sx={{ marginRight: "1rem" }}
        >
          Save
        </Button>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default ChecklistForm;
